/**
 * Catalogos estaticos de la ficha de cata.
 * Fuente de verdad: /specs/data-model.md (seccion Catalogos) + /app/lib/types.ts
 * (prototipo web original).
 */

import type { ColorPrincipal, FichaCata } from './types'
import { randomUUID } from './uuid'

export interface GrupoAromas {
  grupo: string
  descriptores: string[]
}

export const GRUPOS_AROMAS: readonly GrupoAromas[] = [
  {
    grupo: 'Floral',
    descriptores: [
      'Flores de sauco',
      'Lirio de los valles',
      'Azahar',
      'Jazmin',
      'Gardenia',
      'Violeta',
      'Lavanda',
      'Rosas',
      'Geranios',
      'Hibiscus',
    ],
  },
  {
    grupo: 'Frutal',
    descriptores: [
      'Pina',
      'Mango',
      'Papaya',
      'Lichi',
      'Platano',
      'Sandia',
      'Pera',
      'Manzana',
      'Membrillo',
      'Albaricoque',
      'Melocoton',
      'Ciruela',
      'Limon',
      'Lima',
      'Naranja',
      'Pomelo',
      'Fresa',
      'Frambuesa',
      'Arandano',
      'Zarzamora',
    ],
  },
  {
    grupo: 'Tierra',
    descriptores: [
      'Caliza',
      'Pedernal',
      'Piedras humedas',
      'Tiza',
      'Grava',
      'Grafito',
      'Sal',
      'Suelo de bosque',
      'Trufa',
      'Setas',
      'Hojas humedas',
      'Compost',
      'Petricor',
    ],
  },
  {
    grupo: 'Quimico',
    descriptores: ['Combustible', 'Medicamento', 'Caucho', 'Alquitran', 'Plastico'],
  },
  {
    grupo: 'Herbaceas',
    descriptores: [
      'Romero',
      'Salvia',
      'Tomillo',
      'Manzanilla',
      'Hoja de tomate',
      'Eucalipto',
      'Menta',
      'Alcanfor',
      'Aguja de pino',
      'Hojas',
      'Regaliz',
      'Tabaco',
    ],
  },
  {
    grupo: 'Vegetal',
    descriptores: [
      'Hierba',
      'Heno',
      'Paja',
      'Pimiento verde',
      'Esparragos',
      'Brocoli',
      'Aceitunas',
      'Pepino',
    ],
  },
  {
    grupo: 'Especias',
    descriptores: [
      'Pimienta blanca',
      'Pimienta negra',
      'Azafran',
      'Vainilla',
      'Clavo',
      'Canela',
      'Nuez moscada',
      'Cardamomo',
      'Jengibre seco',
    ],
  },
  {
    grupo: 'Madera',
    descriptores: ['Cedro', 'Madera carbonizada', 'Humo', 'Coco', 'Vainilla', 'Tostado', 'Resina'],
  },
  {
    grupo: 'Confiteria',
    descriptores: ['Miel', 'Caramelo', 'Toffee', 'Melaza', 'Chicle'],
  },
  {
    grupo: 'Granos',
    descriptores: ['Nueces', 'Avellanas', 'Almendras', 'Cafe', 'Chocolate'],
  },
  {
    grupo: 'Lacteos',
    descriptores: ['Leche', 'Queso', 'Mantequilla', 'Crema', 'Yogur'],
  },
  {
    grupo: 'Microbiologico',
    descriptores: ['Moho', 'Levadura fresca', 'Lias'],
  },
  {
    grupo: 'Animal',
    descriptores: ['Cuero', 'Lana', 'Carne', 'Sangre'],
  },
  {
    grupo: 'Terciario',
    descriptores: [
      'Cuero',
      'Suelo de bosque',
      'Tierra',
      'Champinon',
      'Caza',
      'Tabaco',
      'Miel',
      'Heno',
      'Ciruelas pasas',
      'Higos',
    ],
  },
  {
    grupo: 'Pasteleria',
    descriptores: ['Pan', 'Pan tostado', 'Bollo', 'Masa', 'Levadura', 'Galleta'],
  },
] as const

/**
 * @deprecated Legacy (form actual de 3 colores). El rediseño usa COLOR_SUBCATEGORIAS.
 * Se conserva para no romper el form interino; la key `ambar` existe solo para
 * satisfacer el tipo (ColorPrincipal ahora incluye 'ambar').
 */
export const COLORES_INTERIOR: Record<Exclude<ColorPrincipal, ''>, readonly string[]> = {
  blanco: ['Verde limon', 'Limon', 'Paja', 'Heno', 'Dorado', 'Ambar', 'Marron'],
  ambar: ['Ambar amarillento', 'Ambar dorado', 'Ambar anaranjado', 'Ambar cobrizo', 'Ambar caoba'],
  rosado: ['Gris', 'Rosa', 'Salmon', 'Naranja', 'Cebolla'],
  tinto: ['Purpura', 'Rubi', 'Granate', 'Rojizo', 'Marron'],
} as const

// ============================================================================
// Rediseño ficha de cata (ver docs/ficha-cata-cambios.md). Catálogos nuevos.
// IDs canónicos (kebab) como claves estables; labels en español para mostrar.
// ============================================================================

export interface OpcionCatalogo {
  id: string
  label: string
}

/** Subcategorías de color por categoría principal (reemplaza COLORES_INTERIOR). */
export const COLOR_SUBCATEGORIAS: Record<Exclude<ColorPrincipal, ''>, readonly OpcionCatalogo[]> = {
  blanco: [
    { id: 'amarillo-verdoso', label: 'Amarillo verdoso' },
    { id: 'amarillo-limon', label: 'Amarillo Limón' },
    { id: 'amarillo-pajizo', label: 'Amarillo Pajizo' },
    { id: 'dorado', label: 'Dorado' },
    { id: 'ambar', label: 'Ámbar' },
  ],
  ambar: [
    { id: 'ambar-amarillento', label: 'Ámbar amarillento' },
    { id: 'ambar-dorado', label: 'Ámbar dorado' },
    { id: 'ambar-anaranjado', label: 'Ámbar anaranjado' },
    { id: 'ambar-cobrizo', label: 'Ámbar cobrizo' },
    { id: 'ambar-caoba', label: 'Ámbar caoba' },
  ],
  rosado: [
    { id: 'gris-claro', label: 'Gris claro' },
    { id: 'rosado', label: 'Rosado' },
    { id: 'piel-salmon', label: 'Piel de Salmón' },
    { id: 'anaranjado', label: 'Anaranjado' },
    { id: 'cobrizo-cebolla', label: 'Cobrizo (piel de cebolla)' },
  ],
  tinto: [
    { id: 'purpura', label: 'Púrpura' },
    { id: 'rubi', label: 'Rubí' },
    { id: 'granate', label: 'Granate' },
    { id: 'rojizo', label: 'Rojizo' },
    { id: 'marron', label: 'Marrón' },
  ],
} as const

export const COLOR_PRINCIPAL_OPCIONES: readonly OpcionCatalogo[] = [
  { id: 'blanco', label: 'Blanco' },
  { id: 'ambar', label: 'Ámbar / Skin' },
  { id: 'rosado', label: 'Rosado' },
  { id: 'tinto', label: 'Tinto' },
] as const

// ---- Taxonomía jerárquica de aromas / sabores (Anexo A) ----
// grupo → familia → (subfamilia | descriptores). Compartida por Nariz y Boca.

export interface SubfamiliaAroma {
  id: string
  label: string
  descriptores: readonly string[]
}
export interface FamiliaAroma {
  id: string
  label: string
  descriptores?: readonly string[]
  subfamilias?: readonly SubfamiliaAroma[]
}
export type GrupoAromaKey = 'primarios' | 'secundarios' | 'terciarios'

export const TAXONOMIA_AROMAS: Record<GrupoAromaKey, readonly FamiliaAroma[]> = {
  primarios: [
    {
      id: 'frutal',
      label: 'Frutal',
      subfamilias: [
        { id: 'citricos', label: 'Cítricos', descriptores: ['Limón', 'Lima', 'Naranja', 'Pomelo', 'Mandarina', 'Kinoto'] },
        { id: 'tropical', label: 'Tropical', descriptores: ['Banana', 'Maracuyá', 'Mango', 'Guayaba', 'Lychee', 'Ananá', 'Kiwi', 'Jaca', 'Melón'] },
        { id: 'fruta-pepita', label: 'Fruta de Pepita', descriptores: ['Pera', 'Manzana Verde', 'Manzana Roja', 'Membrillo'] },
        { id: 'fruta-hueso', label: 'Fruta de Hueso', descriptores: ['Damasco', 'Durazno', 'Pelón', 'Ciruela amarilla', 'Ciruela Roja', 'Ciruela Negra'] },
        { id: 'frutas-rojas', label: 'Bayas · Frutas Rojas', descriptores: ['Frutilla', 'Frambuesa', 'Cereza', 'Guinda', 'Grosella roja', 'Arándano rojo (cranberry)', 'Granada', 'Ciruela roja', 'Rosa mosqueta'] },
        { id: 'fruta-negra', label: 'Bayas · Fruta Negra', descriptores: ['Mora', 'Zarzamora', 'Cassis (grosella negra)', 'Grosella negra', 'Arándano', 'Cereza negra', 'Ciruela negra', 'Higo', 'Baya de saúco', 'Jabuticaba'] },
        { id: 'frutos-secos', label: 'Frutos secos', descriptores: ['Dátiles', 'Higos', 'Pasas de uva', 'Frutas confitadas'] },
      ],
    },
    {
      id: 'floral',
      label: 'Floral',
      subfamilias: [
        { id: 'flores-blancas', label: 'Flores Blancas', descriptores: ['Acacia', 'Flores blancas', 'Flor de saúco', 'Flor de azahar', 'Jazmín', 'Madreselva', 'Gardenia', 'Lirio de los valles', 'Flor de espino'] },
        { id: 'flores-silvestres', label: 'Flores Silvestres', descriptores: ['Manzanilla', 'Lavanda', 'Flor de tilo', 'Retama', 'Brezo', 'Diente de león'] },
        { id: 'flores-azules-violetas', label: 'Flores Azules y Violetas', descriptores: ['Violeta', 'Iris'] },
        { id: 'flores-rojas-rosadas', label: 'Flores Rojas y Rosadas', descriptores: ['Rosa', 'Geranio', 'Hibiscus', 'Lila'] },
        { id: 'flores-secas', label: 'Flores Secas', descriptores: ['Pétalos secos', 'Flores secas', 'Popurrí', 'Rosa seca', 'Lavanda seca'] },
      ],
    },
    { id: 'herbaceo', label: 'Herbáceo', descriptores: ['Romero', 'Tomillo', 'Albahaca', 'Lemongrass', 'Eucalipto', 'Menta', 'Lavanda', 'Eneldo', 'Ciboulette', 'Perejil', 'Hinojo', 'Orégano'] },
    { id: 'vegetal', label: 'Vegetal', descriptores: ['Hoja de tomate', 'Espárrago', 'Pimiento verde', 'Apio', 'Rábano', 'Aceituna', 'Arveja', 'Remolacha'] },
    { id: 'mineral', label: 'Mineral', descriptores: ['Tiza', 'Caliza', 'Pizarra', 'Sílex', 'Canto rodado', 'Roca mojada', 'Lana mojada', 'Concha marina'] },
  ],
  secundarios: [
    { id: 'organico-terroso', label: 'Orgánico/Terroso', descriptores: ['Compost', 'Tierra húmeda', 'Sotobosque', 'Petricor', 'Hojarasca', 'Trufa'] },
    { id: 'especiado', label: 'Especiado', descriptores: ['Sal de apio', 'Cilantro (semilla)', 'Enebro', 'Pimienta blanca', 'Pimienta negra', 'Pimienta de Jamaica'] },
    { id: 'especias-dulces', label: 'Especias Dulces', descriptores: ['Canela', 'Anís', 'Clavo de olor', 'Nuez moscada', 'Vainilla'] },
    { id: 'crianza-madera', label: 'Crianza en Madera', descriptores: ['Vainilla', 'Coco', 'Caramelo', 'Toffee', 'Clavo de olor', 'Nuez moscada', 'Cedro', 'Tostado', 'Café', 'Chocolate', 'Resina', 'Ahumado', 'Madera carbonizada'] },
    { id: 'conversion-malolactica', label: 'Conversión Maloláctica', descriptores: ['Manteca', 'Crema', 'Brioche', 'Yogur'] },
    { id: 'crianza-lias', label: 'Crianza sobre Lías', descriptores: ['Levadura', 'Masa de pan', 'Pan brioche', 'Pan tostado', 'Galleta', 'Bizcocho', 'Pastelería', 'Frutos secos', 'Queso'] },
    { id: 'maceracion-carbonica', label: 'Maceración Carbónica', descriptores: ['Banana', 'Chicle', 'Tutti frutti', 'Frutilla', 'Frambuesa', 'Cereza', 'Pera'] },
    { id: 'botrytis', label: 'Botrytis (Podredumbre noble)', descriptores: ['Miel', 'Cera de abejas', 'Azafrán', 'Jengibre', 'Pan de especias'] },
  ],
  terciarios: [
    { id: 'oxidativos', label: 'Oxidativos', descriptores: ['Almendra', 'Mazapán', 'Avellana', 'Nuez', 'Miel', 'Caramelo', 'Toffee', 'Café', 'Chocolate'] },
    {
      id: 'evolucion',
      label: 'Evolución',
      subfamilias: [
        { id: 'vinos-blancos', label: 'Vinos Blancos', descriptores: ['Miel', 'Manzanilla', 'Petróleo/queroseno', 'Paja', 'Heno', 'Frutos secos', 'Champiñón', 'Trufa', 'Aceite de oliva', 'Damasco seco', 'Manzana deshidratada', 'Banana deshidratada', 'Mermelada de naranja', 'Canela', 'Jengibre', 'Nuez moscada'] },
        { id: 'vinos-tintos', label: 'Vinos Tintos', descriptores: ['Tierra húmeda', 'Hojarasca', 'Trufa', 'Cuero', 'Caza', 'Tabaco', 'Té negro', 'Balsámico', 'Tomate seco', 'Remolacha', 'Carne', 'Umami', 'Establo', 'Ciruela pasa', 'Higo seco', 'Pasas de uva', 'Mora deshidratada', 'Arándano deshidratado', 'Mora cocida', 'Ciruela cocida'] },
      ],
    },
  ],
} as const

export const GRUPOS_AROMA_LABELS: Record<GrupoAromaKey, string> = {
  primarios: 'Primarios',
  secundarios: 'Secundarios',
  terciarios: 'Terciarios',
}

// ---- Defectos (Nariz + Boca) ----
export const DEFECTOS: readonly OpcionCatalogo[] = [
  { id: 'tca', label: 'TCA (olor a corcho)' },
  { id: 'oxidacion', label: 'Oxidación (miel, frutos secos)' },
  { id: 'brettanomyces', label: 'Brettanomyces (establo/disolvente)' },
  { id: 'acidez-volatil', label: 'Acidez volátil (vinagre/acetona)' },
  { id: 'reduccion', label: 'Reducción (sulfuro)' },
] as const

// ---- Descriptores complementarios (Boca) ----
export const COMPLEMENTARIOS = {
  acidez: ['Plana', 'Integrada', 'Refrescante', 'Crujiente', 'Vibrante'],
  alcohol: ['Integrado', 'Armónico', 'Pronunciado', 'Cálido', 'Alcohólico'],
  cuerpo: ['Ligero', 'Fluido', 'Redondo', 'Oleoso', 'Pesado'],
  taninos: ['Suave', 'Sedoso', 'Pulido', 'De grano fino', 'Calcáreo', 'Firme', 'Pronunciado', 'Grueso', 'Secante', 'Verde', 'Integrado'],
} as const

// ---- Vinificación (Conclusión) ----
export const VINIFICACION: Record<'tranquilo' | 'espumoso' | 'dulce', readonly string[]> = {
  tranquilo: ['Clásico', 'Moderno', 'Oxidativo', 'Fortificado', 'Contacto con lías', 'Contacto con pieles'],
  espumoso: ['Tradicional', 'Charmat', 'Ancestral', 'Carbonatado'],
  dulce: ['Late Harvest', 'Passito', 'Ice Wine', 'Botrytis', 'Encabezado', 'Tawny', 'Ruby'],
} as const

export const CLIMA_TIPO_OPCIONES: readonly OpcionCatalogo[] = [
  { id: 'continental', label: 'Continental' },
  { id: 'mediterraneo', label: 'Mediterráneo' },
  { id: 'oceanico', label: 'Oceánico' },
] as const

export const SERVICIO_OPCIONES: readonly OpcionCatalogo[] = [
  { id: 'directo', label: 'Servir directo' },
  { id: 'airear', label: 'Airear' },
  { id: 'decantar', label: 'Decantar' },
] as const

export const POTENCIAL_GUARDA: readonly OpcionCatalogo[] = [
  { id: '1-3', label: '1–3 años' },
  { id: '3-6', label: '3–6 años' },
  { id: '6-9', label: '6–9 años' },
  { id: '10+', label: '10 o más' },
  { id: 'otro', label: 'Otro' },
] as const

/** Rangos de temperatura de consumo. */
export const TEMPERATURAS: readonly OpcionCatalogo[] = [
  { id: 'muy-frio', label: '4–6 °C · Muy frío' },
  { id: 'frio', label: '7–9 °C · Frío' },
  { id: 'fresco', label: '10–12 °C · Fresco' },
  { id: 'ligeramente-fresco', label: '13–15 °C · Ligeramente fresco' },
  { id: 'ambiente', label: '16–18 °C · Ambiente' },
] as const

// ---- Catálogo de variedades de uva (ver docs/cepas-catalogo.md) ----
// Nombre canónico + color. Búsqueda sin acentos y por alias se resuelve en la UI.
export interface Cepa {
  nombre: string
  color: 'tinta' | 'blanca' | 'rosada'
}

export const CEPAS: readonly Cepa[] = [
  // Tintas
  ...[
    'Aglianico', 'Aleatico', 'Alfrocheiro', 'Alicante Bouschet', 'Ancellotta', 'Areni', 'Aramon', 'Baga',
    'Barbera', 'Blaufränkisch', 'Bobal', 'Bonarda', 'Bovale', 'Brachetto', 'Cabernet Franc',
    'Cabernet Sauvignon', 'Caiño Tinto', 'Caladoc', 'Callet', 'Cannonau', 'Carignan', 'Carménère',
    'Casavecchia', 'Castelão', 'Cesanese', 'Charbono', 'Ciliegiolo', 'Cinsault', 'Corvina', 'Corvinone',
    'Counoise', 'Croatina', 'Dolcetto', 'Dornfelder', 'Duras', 'Durif', 'Fer Servadou', 'Feteasca Neagra',
    'Frappato', 'Freisa', 'Gaglioppo', 'Gamay', 'Garnacha', 'Garnacha Tintorera', 'Graciano', 'Grignolino',
    'Grolleau', 'Groppello', 'Kadarka', 'Kékfrankos', 'Lagrein', 'Lambrusco', 'Lemberger', 'Listán Negro',
    'Malbec', 'Marselan', 'Marzemino', 'Mataró', 'Mavrodaphne', 'Mazuelo', 'Mencía', 'Merlot', 'Molinara',
    'Monastrell', 'Mondeuse', 'Montepulciano', 'Moristel', 'Mourvèdre', 'Nebbiolo', 'Negroamaro',
    "Nero d'Avola", 'Nerello Mascalese', 'Nielluccio', 'País', 'Petit Verdot', 'Petite Sirah',
    'Pinot Meunier', 'Pinot Noir', 'Pinotage', 'Plavac Mali', 'Poulsard', 'Prieto Picudo', 'Primitivo',
    'Refosco', 'Rondinella', 'Rossese', 'Rubin', 'Ruché', 'Sagrantino', 'Sangiovese', 'Saperavi',
    'Schiava', 'Schioppettino', 'St. Laurent', 'Susumaniello', 'Syrah', 'Tannat', 'Tempranillo',
    'Teroldego', 'Terrano', 'Tinta Amarela', 'Tinta Barroca', 'Tinta Cão', 'Tinta Negra', 'Tinta Roriz',
    'Tinto Fino', 'Touriga Franca', 'Touriga Nacional', 'Trepat', 'Trincadeira', 'Trollinger',
    'Uva di Troia', 'Vespolina', 'Vranac', 'Xinomavro', 'Zinfandel', 'Zweigelt',
  ].map((nombre) => ({ nombre, color: 'tinta' as const })),
  // Blancas
  ...[
    'Airén', 'Albariño', 'Albillo', 'Aligoté', 'Ansonica', 'Arinto', 'Arneis', 'Assyrtiko', 'Bical',
    'Bourboulenc', 'Chardonnay', 'Chasselas', 'Chenin Blanc', 'Clairette', 'Cortese', 'Doña Blanca',
    'Encruzado', 'Falanghina', 'Fernão Pires', 'Fiano', 'Furmint', 'Garganega', 'Garnacha Blanca',
    'Gewürztraminer', 'Godello', 'Greco', 'Grillo', 'Gros Manseng', 'Grüner Veltliner', 'Hondarrabi Zuri',
    'Kerner', 'Loureiro', 'Macabeo', 'Malagousia', 'Malvasía', 'Manseng', 'Marsanne',
    'Melon de Bourgogne', 'Merseguera', 'Moscatel', 'Moscatel de Alejandría', 'Müller-Thurgau',
    'Muscadelle', 'Palomino', 'Pardina', 'Parellada', 'Pecorino', 'Pedro Ximénez', 'Petit Manseng',
    'Picpoul', 'Pinot Blanc', 'Pinot Gris', 'Prosecco', 'Riesling', 'Rkatsiteli', 'Roussanne',
    'Sauvignon Blanc', 'Sauvignon Gris', 'Savagnin', 'Sémillon', 'Sercial', 'Silvaner', 'Tocai Friulano',
    'Torrontés', 'Treixadura', 'Trebbiano', 'Ugni Blanc', 'Verdejo', 'Verdelho', 'Verdicchio',
    'Vermentino', 'Vernaccia', 'Vidal Blanc', 'Vinhão', 'Viognier', 'Viura', 'Weissburgunder',
    'Welschriesling', 'Xarel·lo', 'Zibibbo',
  ].map((nombre) => ({ nombre, color: 'blanca' as const })),
] as const

/** Sinónimos → nombre canónico, para la búsqueda del autocomplete. */
export const CEPA_SINONIMOS: Record<string, string> = {
  Shiraz: 'Syrah',
  'Côt': 'Malbec',
  Grenache: 'Garnacha',
  'Grenache Blanc': 'Garnacha Blanca',
  Mourvedre: 'Monastrell',
  'Alicante Bouschet': 'Garnacha Tintorera',
  Cariñena: 'Carignan',
  'Ull de Llebre': 'Tempranillo',
  Cencibel: 'Tempranillo',
  'Tinta del País': 'Tempranillo',
  'Tinta de Toro': 'Tempranillo',
  Alvarinho: 'Albariño',
  'Pinot Grigio': 'Pinot Gris',
  'Pinot Bianco': 'Pinot Blanc',
  Glera: 'Prosecco',
  Sylvaner: 'Silvaner',
  Friulano: 'Tocai Friulano',
  Inzolia: 'Ansonica',
  'Maria Gomes': 'Fernão Pires',
  Muscadet: 'Melon de Bourgogne',
  Vernatsch: 'Schiava',
  'Maria Gomez': 'Fernão Pires',
}

/**
 * Crea una ficha vacia con los defaults del dominio.
 */
export function createEmptyFicha(): FichaCata {
  const now = new Date().toISOString()
  return {
    id: randomUUID(),
    createdAt: now,
    updatedAt: now,
    version: 1,
    vino: '',
    cosecha: '',
    origen: '',
    alcPorcentaje: '',
    apariencia: {
      claridad: '',
      brillo: '',
      colorPrincipal: '',
      intensidad: '',
      colorInterior: '',
      variacionBorde: '',
      viscosidad: '',
      otrasObservaciones: '',
      // rediseño. -1 = "sin definir" (el slider se ve centrado en 50 pero no se
      // persiste como dato hasta que el usuario lo toca; ver ScoreSlider).
      claridadScore: -1,
      brilloScore: -1,
      viscosidadScore: -1,
    },
    nariz: {
      limpioDefectuoso: '',
      intensidad: '',
      sensacionAlcohol: '',
      gruposAromas: [],
      descriptoresAromas: '',
      influenciaRoble: '',
      nivelMadurez: '',
      // rediseño
      defectos: { seleccion: [], otro: '' },
      aromas: {},
      condicionFruta: -1,
    },
    gusto: {
      limpioDefectuoso: '',
      secoDulce: '',
      co2: '',
      cuerpo: '',
      nivelAcidez: '',
      intensidad: '',
      gruposSabor: [],
      descriptoresSabor: '',
      confirmacionRoble: '',
      nivelMadurez: '',
      taninos: '',
      nivelAlcohol: '',
      complejidad: '',
      finalPersistencia: '',
      // rediseño
      defectos: { seleccion: [], otro: '' },
      co2Intensidad: '',
      acidezEscala: '',
      acidezComplementario: [],
      acidezComplementarioOtro: '',
      alcoholEscala: '',
      alcoholComplementario: [],
      alcoholComplementarioOtro: '',
      cuerpoComplementario: [],
      cuerpoComplementarioOtro: '',
      taninosNivel: '',
      taninosComplementario: [],
      taninosComplementarioOtro: '',
      persistenciaEscala: '',
      sabores: {},
      condicionFruta: -1,
    },
    conclusiones: {
      estiloVinificacion: '',
      climaFrioCalido: '',
      variedadUva: '',
      apelacion: '',
      region: '',
      pais: '',
      rangoEdad: '',
      anada: '',
      etiquetaVino: '',
      productor: '',
      calidad: '',
      potencialGuarda: '',
      temperatura: '',
      seleccionCopas: '',
      decantarAirear: '',
      descripcionPlatoRecomendado: '',
      razonMaridaje: '',
      // rediseño
      vinificacionCategoria: '',
      vinificacionEstilo: [],
      vinificacionOtro: '',
      climaTipo: '',
      variedadUvas: [],
      variedadUvaOtros: '',
      servicio: '',
      potencialGuardaOtro: '',
    },
  }
}
